import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Card variants — handoff EXAMPLES.tsx §5 + CLAYMORPHISM.md
 * Surfaces hero (mint/ink/clay/lilac) usam gradient radial + linear, border branca
 * translúcida no light e shadow tonal — claymorphism estratégico.
 *
 * Regra: 2-4 floating cards max por tela (CLAYMORPHISM §4).
 */
const cardVariants = cva(
  'rounded-3 border transition-all duration-fast ease-out relative overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-card border-card-stroke shadow-1',
        soft: 'bg-card-soft border-transparent shadow-pressed',
        float:
          'bg-[linear-gradient(180deg,var(--card),#F8F2E5)] border-white/60 shadow-clay',
        mint:
          'text-on-mint border-white/45 shadow-clay-mint ' +
          'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.5),transparent_60%),linear-gradient(160deg,#D2E8DA_0%,var(--mint)_70%)]',
        ink:
          'text-on-ink border-white/5 shadow-clay-ink ' +
          'bg-[linear-gradient(160deg,var(--ink-soft)_0%,var(--ink-deep)_100%)]',
        clay:
          'text-on-clay border-white/45 shadow-clay-clay ' +
          'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.55),transparent_60%),linear-gradient(160deg,#F5DCC4_0%,var(--clay)_70%)]',
        lilac:
          'text-[#2A2240] border-white/45 shadow-clay ' +
          'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.5),transparent_60%),linear-gradient(160deg,#E1D7F0_0%,var(--lilac)_70%)]',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'font-display text-[18px] font-semibold tracking-[-0.015em] leading-[1.3]',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-fg-muted', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-end gap-3 p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';
